import { Injectable, OnDestroy } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { HttpClient, HttpParams, HttpHeaders, HttpBackend } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  signIn,
  confirmSignUp,
  getCurrentUser,
  updateUserAttributes,
  resetPassword,
  fetchUserAttributes,
  confirmSignIn,
  signOut,
} from 'aws-amplify/auth';
import { AWSEnum } from '../shared/enums/aws.enum';
import { GetUserQuery, RoleEnum, EntityTypeEnum, ModelUserFilterInput } from '../API.service';
import { RootEntityContextService } from 'app/shared/root-entity-context.service';
import { NGXLogger } from 'ngx-logger';
import { UtilsService } from 'app/shared/utils.service';
import { Subject, Subscription } from 'rxjs';
import { getFederatedIdentityProvider } from 'app/shared/enums/cognito.enum';
import * as CryptoJS from 'crypto-js';
import { CUSTOMAPIService, CreateUserInput, UpdateUserInput } from 'app/custom-api.service';
import { ClientLambdaService } from 'app/shared/client-lambda.service';
import { environment } from 'environments/environment';
import { GeneralNotifyTypeEnum } from 'app/shared/enums/email-notifications.enum';
import { AuthSessionCacheService } from 'app/shared/auth-session-cache.service';
import { AppLevelRoleEnum } from 'app/shared/enums/appLevelRoles.enum';
export interface Credentials {
  email: string;
  password: string;
  remember?: boolean;
}

const CYGOV_STORAGE = 'cygov_storage';
const COGNITO_USER = 'isCognitoUser';
const AD_USER = 'isADUser';
const ACCESS_TOKEN = 'ad-token';

@Injectable()
export class AuthService implements OnDestroy {
  storage: Storage = localStorage;
  user: GetUserQuery;
  authUser: any = null; // This is for cognito user
  userRole: any;
  // In-flight de-dup: concurrent bootstrap callers share a single getCurrentUser()/GetRole fetch.
  private _getCurrentUserPromise: Promise<GetUserQuery> | null = null;
  private _parentRoleInFlight: { [roleId: string]: Promise<any> } = {};
  // Single cached by-id user lookup shared across the app (in-flight de-duplicated).
  private _userByIdCache: { [id: string]: GetUserQuery } = {};
  private _userByIdInFlight: { [id: string]: Promise<GetUserQuery> } = {};
  tokenHTTP: HttpClient; // We need a separate instance of httpClient to bypass the interceptors.
  credentials;
  isFirstLogin: boolean = false;
  welcomeGuideStatus: Subject<boolean> = new Subject<boolean>();
  headerLoaderSubscriber: Subject<boolean> = new Subject<boolean>();
  private subscriptionList: Subscription[];
  currentToken: any;
  redirectURL: string = '';

  constructor(
    private router: Router,
    private handler: HttpBackend,
    private rootEntityContextService: RootEntityContextService,
    private logger: NGXLogger,
    private customApi: CUSTOMAPIService,
    private toastrService: ToastrService,
    private clientLambdaService: ClientLambdaService,
    private authSessionCacheService: AuthSessionCacheService
  ) {
    if (['/', '/login'].includes(this.router.url)) {
      return;
    }
    this.initializeSessionListener();

    this.customApi.webSocketConnectionListner$.subscribe(hubState => {
      if (hubState?.isConnectionClosed && this.subscriptionList?.length) {
        this.subscriptionList.forEach(listener => listener?.unsubscribe());
        this.subscriptionList = [];
        this.subscriptionList = [this.onDeleteUserListener(this.currentToken)];
      }
    });
    this.tokenHTTP = new HttpClient(this.handler);
    if (sessionStorage.getItem(CYGOV_STORAGE)) {
      this.storage = sessionStorage;
    } else {
      this.storage = localStorage;
    }
  }

  private async initializeSessionListener(): Promise<void> {
    try {
      const currentSession: any = await this.authSessionCacheService.getSession();
      if (!currentSession?.tokens?.accessToken) {
        return;
      }
      this.currentToken = currentSession.tokens.accessToken.toString();
      this.subscriptionList = [this.onDeleteUserListener(this.currentToken)];
    } catch {
      // No authenticated Amplify session yet, skip Cognito-dependent listeners.
    }
  }

  get isCognitoUser(): boolean {
    try {
      const isCognitoUser = JSON.parse(this.storage.getItem(COGNITO_USER));
      return isCognitoUser ? isCognitoUser : null;
    } catch {
      return null;
    }
  }

  setIsCognitoUser(value: boolean): void {
    this.storage.setItem(COGNITO_USER, String(value));
  }

  get isADUser(): boolean {
    try {
      const isADUser = JSON.parse(this.storage.getItem(AD_USER).toString());
      return isADUser ? isADUser : null;
    } catch {
      return null;
    }
  }

  setIsADUser(value: boolean): void {
    this.storage.setItem(AD_USER, String(value));
  }

  get accessToken(): string {
    try {
      const accessToken = this.storage.getItem(ACCESS_TOKEN);
      return accessToken ? accessToken : null;
    } catch {
      return null;
    }
  }

  setAccessToken(value: string): void {
    this.storage.setItem(ACCESS_TOKEN, value);
  }

  get currentAuthUser(): any {
    return this.authUser;
  }

  get isStorageLocal(): boolean {
    return this.storage === localStorage;
  }

  async isAuthenticated(): Promise<boolean> {
    return !!(await this.getCurrentUser());
  }

  async updateUserAttributes(attributes: any): Promise<any> {
    this.logger.log('updateUserAttributes');
    return await updateUserAttributes({
      userAttributes: attributes,
    });
  }

  async setFirstPassword(email: string, password: string, userAttributes: any): Promise<string> {
    this.logger.log('In setFirstPassword');
    const currentUser = this.authUser;
    const loggedUser = await confirmSignIn({
      challengeResponse: password,
      options: {
        userAttributes,
      },
    });
    this.isFirstLogin = true;
    this.setIsCognitoUser(true);
    this.authUser = loggedUser;
    return JSON.stringify(loggedUser.nextStep);
  }

  async signIn(credentials: Credentials): Promise<string> {
    const { email: username, password } = credentials;
    const domain = window.location.origin;
    const clientMetadata = { domain };
    this.setIsCognitoUser(true);
    try {
      this.authUser = await signIn({
        username,
        password,
        options: { clientMetadata, authFlowType: 'CUSTOM_WITH_SRP' },
      });

      return this.authUser.nextStep.signInStep;
    } catch (e) {
      this.logger.error('signIn - Error: ', e);
      return Promise.reject(e);
    }
  }

  async loginCodeConfirm(codeFromUser?: string): Promise<GetUserQuery> {
    try {
      const authUser = this.authUser;
      if (!authUser) {
        console.log('No Auth User');
      }
      this.authUser = await confirmSignIn({
        challengeResponse: codeFromUser,
      });
      return await this.getCurrentUser();
    } catch (e) {
      this.logger.error('twoFactorPhase - Error: ', e);
      throw Error(e);
    }
  }

  async logOut(): Promise<void> {
    try {
      this.user = null;
      this.userRole = null;
      this.rootEntityContextService.setAccessContext(null, null);
      this._getCurrentUserPromise = null;
      this._userByIdCache = {};
      this._userByIdInFlight = {};
      const currentSession: any = await this.authSessionCacheService.getSession();

      if (!currentSession) {
        this.logger.error('logOut - Error: Failed to get current session state');
        return Promise.reject('logOut - Error: Failed to get current session state');
      }

      await this.clientLambdaService.invokeLambda('revokeTokens', {
        token: currentSession?.tokens.accessToken.toString(),
      });
      this.redirectURL = null;
      localStorage.clear();
      sessionStorage.clear();
      await signOut({ global: true });
    } catch (e) {
      this.logger.error('logOut - Error: ', e);
      return Promise.reject(e);
    }
  }

  async sendInviteEmail(
    userId: string,
    roleId: string,
    userEmail: string,
    userName: string,
    isWelcomeEmail: boolean = false
  ): Promise<string> {
    try {
      const domain = window.location.origin;
      const payload = {
        userId,
        roleId,
        userEmail,
        userName,
        domain,
        isWelcomeEmail,
      };
      await this.customApi.SendInviteEmail(payload);
    } catch (e) {
      this.logger.error('sendInviteEmail - Error: ', e);
      return Promise.reject(e);
    }
  }

  async createUser(userInput: CreateUserInput): Promise<any> {
    await this.customApi.CreateUser(userInput);
    // let user-list caches (e.g. UserService's by-role lists) know the roster changed
    this.userRecordsChanged$.next();
  }

  async addUserToGroup(groupName) {
    const groupInput = { id: groupName, desc: groupName };
    const addCognitoGroupRes = await this.customApi.AddCognitoGroup({
      ...groupInput,
      shouldAddUser: true,
    });
    return addCognitoGroupRes;
    // this.logger.log('AddCognitoGroup: ', addCognitoGroupRes);
  }

  async deleteUserFromCognito(user: any) {
    const result = await this.customApi.DeleteCognitoUser(user);
    if (result) {
      this.authUser = result ? result : null;
    }
    this.logger.log('Cognito user deleted');
  }

  async removeUser(userId: string): Promise<void> {
    await this.customApi.DeleteUser({ id: userId });
    this.invalidateUserCache(userId);
    // let user-list caches (e.g. UserService's by-role lists) know the roster changed
    this.userRecordsChanged$.next();
  }

  async updateUser(user: any): Promise<void> {
    await this.updateUserOrRole(user);
  }

  async sendReminderEmail(user: any, collectionDate: number): Promise<any> {
    try {
      const payload = {
        type: GeneralNotifyTypeEnum.SEND_EMAIL_REMINDER,
        userId: user.id,
        userEmail: user.email,
        userName: user.name,
        domain: window.location.origin,
        collectionDate,
      };
      return this.customApi.SendNotification(payload);
    } catch (e) {
      this.logger.error('sendReminderEmail - Error: ', e);
      return Promise.reject(e);
    }
  }

  // Use this function only inside Auth module (login component etc.)
  async getUserByEmail(userEmail: string): Promise<GetUserQuery> {
    try {
      const filter: ModelUserFilterInput = {
        email: { eq: userEmail },
      };
      return (await this.customApi.ListUsers(filter)).items.pop();
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  async queryUserByEmail(email: string): Promise<GetUserQuery> {
    try {
      const user = (await this.customApi.UserByEmail(email.toLowerCase())).items.pop();
      return user;
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  async sendRestartEmail(userEmail: string): Promise<any> {
    this.setIsCognitoUser(true);
    return await resetPassword({ username: userEmail });
  }

  getCurrentUserSync(): GetUserQuery {
    return this.user ? this.user : null;
  }

  // Concurrent bootstrap callers (route guards, resolvers, components) share one in-flight fetch
  // instead of each firing their own GetUser/GetRole. Cleared on settle so later calls re-run.
  async getCurrentUser(): Promise<GetUserQuery> {
    if (!this._getCurrentUserPromise) {
      this._getCurrentUserPromise = this._loadCurrentUser().finally(() => {
        this._getCurrentUserPromise = null;
      });
    }
    return this._getCurrentUserPromise;
  }

  private async _loadCurrentUser(): Promise<GetUserQuery> {
    try {
      // check if user authenticated or not, if not return null
      const userInfo = await this.currentUserInfo();
      if (!userInfo) {
        return;
      }
      this.authUser = await this.getAuthenticatedUser();
      if (this.authUser && !this.user && this.authUser.attributes) {
        const federatedIdentityProviders = getFederatedIdentityProvider();
        // check if username includes in federated identity provider list
        const isFederatedUser = federatedIdentityProviders.some(provider =>
          this.authUser.username.toLowerCase().includes(provider.toLowerCase())
        );
        this.user = isFederatedUser
          ? await this.queryUserByEmail(this.authUser.attributes.email.toLowerCase())
          : await this.customApi.GetUser(this.authUser.username);
      }
      if (this.user && this.user.roleId && !this.userRole) {
        this.userRole = await this.customApi.GetRole(this.user.roleId);
        this.rootEntityContextService.setAccessContext(this.user, this.userRole);
      }
      return this.user;
    } catch (error) {
      console.log('error in auth user', error);
    }
  }

  async currentUserInfo() {
    try {
      const { username, userId: id } = await getCurrentUser();
      const attributes = fetchUserAttributes();
      return {
        id,
        username,
        attributes,
      };
    } catch (e) {}
  }

  async getAuthenticatedUser() {
    const { tokens: session } = await this.authSessionCacheService.getSession();
    const { username, userId } = await getCurrentUser();
    const attributes = await fetchUserAttributes();
    return {
      username,
      userId,
      session,
      attributes,
    };
  }

  // Single cached by-id user lookup (in-flight de-duplicated). Bust via invalidateUserCache().
  async getUserById(id: string): Promise<GetUserQuery> {
    if (!id) {
      return null;
    }
    if (this._userByIdCache[id] !== undefined) {
      return this._userByIdCache[id];
    }
    if (this._userByIdInFlight[id]) {
      return this._userByIdInFlight[id];
    }
    this._userByIdInFlight[id] = (async () => {
      try {
        const user = await this.customApi.GetUser(id);
        this._userByIdCache[id] = user ?? null;
        return user ?? null;
      } catch (e) {
        console.log('Error getUserById ==>>', e);
        return null;
      } finally {
        delete this._userByIdInFlight[id];
      }
    })();
    return this._userByIdInFlight[id];
  }

  // Emits whenever a user record is created or deleted, so caches that hold lists of users can
  // clear themselves. Lives here (rather than UserService calling back) to avoid a circular dep.
  userRecordsChanged$ = new Subject<void>();

  // Invalidate one (or all) cached by-id user records — call after a user is updated.
  invalidateUserCache(id?: string): void {
    if (id) {
      delete this._userByIdCache[id];
    } else {
      this._userByIdCache = {};
    }
  }

  /**            ( Code optimization and reusability )
   * ( Note ) Remove : This Function is not in use right now as according to new architecture existing user can be
   *  assigned to multiple entities.
   * @param email is type string conatin email
   * @returns boolean
   */
  async isUserPresent(email: string): Promise<boolean> {
    const code = '000000';
    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code,
      });
      return true;
    } catch (error) {
      switch (error.code) {
        case 'UserNotFoundException':
          return false;
        // Below are the other possible codes which shoes user is present
        case 'NotAuthorizedException':
        case 'AliasExistsException':
        case 'CodeMismatchException':
        case 'ExpiredCodeException':
          return true;
        default:
          return true;
      }
    }
  }

  async updateCurrentUser(user: UpdateUserInput): Promise<GetUserQuery> {
    try {
      this.user = (await this.customApi.UpdateUser(user)) as any;
      if (this.user?.id) {
        this._userByIdCache[this.user.id] = this.user;
      }
      return this.user;
    } catch (e) {
      this.logger.error('updateCurrentUser - Error: ', e);
      return Promise.reject(e);
    }
  }

  async resendCode(): Promise<string> {
    try {
      return await this.signIn(this.credentials);
    } catch (e) {
      this.logger.error('Resend Code Error: ', e);
      return Promise.reject(e.message);
    }
  }

  /**
   * Fetch the authentication tokens for the user.
   *
   * @param token string The token  returned by SAML Auth.
   */
  getOAuthTokens(token: string): Promise<Record<string, any>> {
    const params = new HttpParams({
      fromObject: {
        grant_type: AWSEnum.GRANT_TYPE,
        client_id: AWSEnum.CLIENT_ID,
        redirect_uri: AWSEnum.REDIRECT_URI,
        code: token,
      },
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': AWSEnum.CONTENT_TYPE_HEADER,
        Authorization: AWSEnum.AUTHORIZATION_TYPE + ' ' + btoa(AWSEnum.CLIENT_ID + ':' + AWSEnum.CLIENT_SECRET),
      }),
    };

    try {
      const result = this.tokenHTTP.post(AWSEnum.TOKEN_VERIFICATION_ENDPOINT, params, httpOptions).toPromise();
      return result;
    } catch (e) {
      this.logger.error('OAuth get tokens - Error: ', e);
      return Promise.reject(e);
    }
  }

  async navigateByRole(): Promise<void> {
    let nextRoute: string[];
    let extras: any;
    try {
      const user: GetUserQuery = await this.getCurrentUser();

      if (!user || !user.role) {
        this.logger.error('User role is required!');
        await this.router.navigate(['']);
        return Promise.reject();
      }

      let entityId = null;

      if (user.role !== RoleEnum.ADMIN && user.role !== RoleEnum.MSSP) {
        if (user.entityId) {
          // if one entity id in permission ok to navigate
          // if multiple present navigate to first one
          entityId = user.entityId;
        } else {
          this.logger.error('User has no Permission!');
          await this.router.navigate(['']);
          return Promise.reject();
        }
      }
      const entity = entityId ? await this.rootEntityContextService.getSubEntity(entityId) : null;
      switch (user.role) {
        case RoleEnum.ADMIN:
          nextRoute = ['clients'];
          break;
        case RoleEnum.MSSP:
          nextRoute = ['clients'];
          break;
        case RoleEnum.LEADER:
          // if one entity id in permission ok to navigate
          // if multiple present navigate to first one

          // if user is entity leader and isNetskope is enable then re route to executive summary (hotfix)
          if (entity.entityType === EntityTypeEnum.ROOT_ENTITY && UtilsService.isNetskope) {
            nextRoute = [`board-netskope/${entityId}/executive-summary`];
          } else if (entity.entityType === EntityTypeEnum.ROOT_ENTITY) {
            nextRoute = [`first-party/${entityId}/upperdeck`];
          } else {
            nextRoute = UtilsService.isCRB
              ? [`first-party/${entity.parentId}/collection`, entity.id]
              : [`first-party/${entity.parentId}/multi-entity`, entity.id];
          }
          break;
        case RoleEnum.PARTICIPANT:
          // for now navigate to first entity
          nextRoute = [`first-party/${entity.parentId}/collection`, entity.id];
          break;
        case RoleEnum.VENDOR:
          nextRoute = [`first-party/${entity.parentId}/collection`, entity.id];
          extras = {
            queryParams: { userId: user.id },
          };
          break;

        default:
          nextRoute = ['**'];
      }
      await this.router.navigate(nextRoute, extras);
    } catch (e) {
      this.logger.error('navigateByRole - Error: ', e);
      await this.router.navigate(['']);
      return Promise.reject(e);
    }
  }

  async hasPermission(minRole: RoleEnum, entityId?: string, subEntityId?: string): Promise<boolean> {
    try {
      const user: GetUserQuery = await this.getCurrentUser();
      let hasPermission = false;
      if (user) {
        switch (minRole) {
          case RoleEnum.ADMIN:
            hasPermission = user.role === RoleEnum.ADMIN;
            break;
          case RoleEnum.MSSP:
            hasPermission = user.role === RoleEnum.ADMIN || user.role === RoleEnum.MSSP;
            break;
          case RoleEnum.LEADER:
            hasPermission =
              user.role === RoleEnum.ADMIN ||
              user.role === RoleEnum.MSSP ||
              (user.role === RoleEnum.LEADER &&
                entityId &&
                (user.entityId === entityId || user.entityId === subEntityId));
            break;
          case RoleEnum.PARTICIPANT:
            hasPermission =
              user.role === RoleEnum.ADMIN ||
              user.role === RoleEnum.MSSP ||
              user.role === RoleEnum.LEADER ||
              (user.role === RoleEnum.PARTICIPANT &&
                entityId &&
                subEntityId &&
                (user.entityId === entityId || user.entityId === subEntityId));
            break;
          case RoleEnum.VENDOR:
            hasPermission =
              user.role === RoleEnum.ADMIN ||
              user.role === RoleEnum.MSSP ||
              user.role === RoleEnum.LEADER ||
              (user.role === RoleEnum.VENDOR &&
                entityId &&
                subEntityId &&
                (user.entityId === entityId || user.entityId === subEntityId));
            break;
        }
      } else {
        this.logger.log('No user is found');
      }

      return Promise.resolve(hasPermission);
    } catch (e) {
      this.logger.error('hasPermission - Error: ', e);
      return Promise.reject(e);
    }
  }

  isEntityIdInUrl(url: string, entityId: string): boolean {
    let isIncluded = false;
    if (entityId && url.includes(entityId)) {
      isIncluded = true;
    }
    return isIncluded;
  }

  isAdmin(): boolean {
    return this.user && this.user.role === RoleEnum.ADMIN;
  }

  async addNewParticipant(newUser): Promise<any> {
    const inputUser = JSON.stringify(newUser);
    const domain = window.location.origin;
    return await this.customApi.AddCognitoUser({
      inputUser,
      domain,
    });
  }

  getCurrentEnvironment() {
    return environment.name;
  }

  getRegion() {
    return environment.region;
  }

  clearCache(): void {
    this.user = null;
  }

  setHeaderLoaderStatus(status) {
    this.headerLoaderSubscriber.next(status);
  }

  isFirstTimeUser() {
    return this.isFirstLogin && this.isAdmin() && !UtilsService.isBnB;
  }

  setWelcomeGuideStatus(event) {
    this.welcomeGuideStatus.next(event);
  }

  ngOnDestroy(): void {
    this.subscriptionList.forEach(listener => listener.unsubscribe());
    this.clearCache();
  }

  // Temporary methods
  /**
   * to check if the current user is entity leader or sub-entity leader
   */
  isNotFromHigherRoles(): boolean {
    // handling the case for default roles.
    if (
      this.userRole &&
      this.userRole.name.toLowerCase() !== AppLevelRoleEnum.ADMIN &&
      this.userRole.name.toLowerCase() !== AppLevelRoleEnum.MSSP &&
      this.userRole.name.toLowerCase() !== AppLevelRoleEnum.SUPER_ENTITY_LEADER &&
      this.userRole.name.toLowerCase() !== AppLevelRoleEnum.ENTITY_LEADER &&
      this.userRole.defaultOrEntityId === 'default'
    ) {
      return true;
    } else if (this.userRole && this.userRole.defaultOrEntityId !== 'default' && !this.userRole.isRootEntityAccess) {
      // handling the case for default roles.
      return true;
    } else {
      return false;
    }
  }

  /**
   * to check if user is participant or not.
   */
  isParticipant(): boolean {
    return this.userRole && this.userRole.name && this.userRole.name.toLowerCase() === 'participant' ? true : false;
  }

  /**
   *
   * @params parentRoleId is the role ID of the current role.
   * @returns the parent role of the current Role.
   */
  async getParentRole(parentRoleId: string): Promise<any> {
    if (!parentRoleId) {
      return;
    }
    // In-flight de-dup so concurrent callers for the same parent role share one GetRole.
    if (this._parentRoleInFlight[parentRoleId]) {
      return this._parentRoleInFlight[parentRoleId];
    }
    this._parentRoleInFlight[parentRoleId] = (async () => {
      try {
        return await this.customApi.GetRole(parentRoleId);
      } catch (e) {
        console.log('error in getting the parent role', e);
      } finally {
        delete this._parentRoleInFlight[parentRoleId];
      }
    })();
    return this._parentRoleInFlight[parentRoleId];
  }

  async updateUserOrRole(user, byPassAuth = false) {
    try {
      await this.getCurrentUser();
      user = {
        userRole: this.userRole?.name,
        userDetail: user,
        byPassAuth,
      };
      const encyptedData = CryptoJS.AES.encrypt(JSON.stringify(user), 'cygovSecretKey2023').toString();
      const { Payload } = await this.clientLambdaService.invokeLambda('userAuthorizer', encyptedData);
      const response = JSON.parse(Payload.transformToString());
      if (response.statusCode === 200) {
        const body = JSON.parse(response.body).data;
        this.toastrService.success('User Updated');
        return body;
      }
      return Promise.reject('Error while User Updated');
    } catch (e) {
      this.toastrService.error('Error while User Updated');
      console.log('Error - userAuthMiddleware');
    }
  }

  onDeleteUserListener(token): any {
    return this.customApi.OnDeleteUserListener(token).subscribe({
      next: (res: any) => {
        if (res) {
          const deleteUser = res.data.onDeleteUser;
          if (deleteUser && deleteUser.id === this.user.id) {
            this.toastrService.success('This User Has Been Deleted');
            this.logOut()
              .then(() => {
                this.toastrService.success('Logged out');
                window.location.reload();
              })
              .catch(err => {
                console.error('Log out failed: ', err);
                this.toastrService.error('Log out failed');
              });
          }
        }
      },
      error: err => {
        console.log('Subscription error: ', err);
      },
    });
  }

  async checkTokensValidity() {
    try {
      const session = (await this.authSessionCacheService.getSession())?.tokens;
      if (!session) {
        return false;
      }

      const accessToken = session.accessToken;
      const currentTime = Math.floor(Date.now() / 1000);
      const accessTokenExp = accessToken.payload.exp;
      // Check access token validity
      const isAccessTokenValid = currentTime < accessTokenExp;
      // Calculate refresh token validity
      const refreshTokenIssuedAt = session.idToken.payload.iat;
      const refreshTokenValidityPeriod = 1 * 24 * 60 * 60; // 1 day in seconds
      const refreshTokenExp = refreshTokenIssuedAt + refreshTokenValidityPeriod;
      const isRefreshTokenValid = currentTime < refreshTokenExp;
      if (!isAccessTokenValid && !isRefreshTokenValid) {
        console.log('Both tokens are expired.');
        return false;
      }
      return true;
    } catch (error) {}
  }

  updateRedirectURL(url: string) {
    this.redirectURL = url;
  }
}
